import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Facebook, Instagram } from "lucide-react";
import { BLOG_POSTS, fadeInUp, staggerContainer } from "../data/constants";

export const BlogSection = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [posts, setPosts] = useState(BLOG_POSTS);
  const [subscribeMessage, setSubscribeMessage] = useState("");

  // Fetch posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?limit=4`);
        if (response.ok) {
          const data = await response.json();
          // Handle new paginated response format
          const postsArray = data.posts || data;
          if (postsArray.length > 0) {
            setPosts(postsArray);
          }
        }
      } catch (error) {
        console.log("Using static posts");
      }
    };
    fetchPosts();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      setSubscribeMessage(data.message);
      setSubscribed(true);
      setEmail("");
      setTimeout(() => {
        setSubscribed(false);
        setSubscribeMessage("");
      }, 3000);
    } catch (error) {
      setSubscribed(true);
      setSubscribeMessage("¡Gracias por suscribirte!");
      setEmail("");
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getPostDate = (post) => {
    return post.created_at || post.date;
  };

  return (
    <section id="blog" className="py-24 bg-gray-50" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-orange-500 font-semibold uppercase tracking-wider"
          >
            Blog & Noticias
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-gray-900 mt-4"
          >
            Mantente Informado
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
            Tips, noticias y novedades sobre tecnología e internet para mantenerte conectado.
          </motion.p>
        </motion.div>

        {/* Blog Posts Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {posts.slice(0, 4).map((post) => (
            <motion.article
              key={post.id}
              variants={fadeInUp}
            >
              <Link
                to={`/blog/${post.id}`}
                className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 card-hover group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(getPostDate(post))}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.read_time || post.readTime}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
                  <div className="mt-4 text-orange-500 font-medium text-sm group-hover:underline">
                    Leer más →
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </motion.div>

        {/* Ver todos link */}
        <div className="text-center mb-16">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-orange-500 font-semibold hover:text-orange-600 transition-colors"
          >
            Ver todos los artículos
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Newsletter Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h3 className="text-3xl font-bold mb-4">Suscríbete a nuestro Newsletter</h3>
              <p className="text-white/90 mb-4">
                Recibe tips, promociones exclusivas y las últimas noticias sobre tecnología e internet directamente en tu correo.
              </p>
              <div className="flex items-center gap-4">
                <span className="text-white/70 text-sm">Síguenos:</span>
                <div className="flex items-center gap-3">
                  <a
                    href="https://facebook.com/flowing.lat"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                    data-testid="social-facebook"
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href="https://instagram.com/flowing.lat"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                    data-testid="social-instagram"
                  >
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href="https://tiktok.com/@flowing.lat"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                    data-testid="social-tiktok"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu correo electrónico"
                  className="flex-1 px-6 py-4 rounded-full bg-white/20 text-white placeholder-white/70 border border-white/30 focus:outline-none focus:border-white transition-colors"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-orange-600 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                  data-testid="newsletter-submit"
                >
                  {subscribed ? "¡Suscrito!" : "Suscribirme"}
                </button>
              </form>
              {subscribed && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-white/90 text-sm mt-3"
                >
                  {subscribeMessage || "¡Gracias por suscribirte! Pronto recibirás nuestras novedades."}
                </motion.p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
